import { r as registerInstance, h, a as Host, d as getElement } from './index-BntG-4Sv.js';
import { d as debounce } from './function-utils-D1ZpXQQS.js';
import { service, AuthenticationService, AuthorizationService, NavigationContextService, AuthenticationStorageService, RuntimeConfigurationContextService, EventService, UserPreferencesService, UserPreferencesContextService, GuideContextService, StorageKey, SubscriptionList, LocalStorageSubscriptionHandlerService, WindowService, navigate, navigateToLoginPage, SecurityContextService, EventName, MainMenuExtensionPoint } from '@ontotext/workbench-api';
import { T as TranslationService } from './translation.service-CyWR_ReZ.js';

const RESPONSIVE_BREAKPOINTS = {
    MD: 768};
/**
 * The width of the window in pixels at which the navbar should collapse.
 */
const WINDOW_WIDTH_FOR_COLLAPSED_NAVBAR = RESPONSIVE_BREAKPOINTS.MD;

const ontoLayoutCss = ":host{display:block;height:100%}.wb-layout{display:grid;grid-template:\"header header\" auto \"nav main\" 1fr \"nav footer\" auto/15rem minmax(200px, 1fr);min-height:100vh;margin:0;}.wb-layout.expanded{grid-template-columns:4.375rem 1fr}.wb-layout.is-embedded{grid-template-columns:1fr;grid-template-areas:\"main\";padding-top:1rem}.wb-layout.hide-navbar{grid-template:\"header\" auto \"main\" 1fr \"footer\" auto/1fr;}.wb-layout.hide-navbar.expanded{grid-template-columns:1fr}.wb-layout main .main-container{margin-left:0}.wb-layout main{grid-area:main}.wb-layout.loading main{position:relative;visibility:hidden}.wb-layout .default-slot-wrapper{display:none}.wb-layout .wb-header{grid-area:header;position:sticky;top:0;z-index:999}.wb-layout nav{grid-area:nav}.wb-layout footer{grid-area:footer}";

const OntoLayout = class {
    // ========================
    // Lifecycle methods
    // ========================
    constructor(hostRef) {
        registerInstance(this, hostRef);
        // ========================
        // Services
        // ========================
        this.authenticationService = service(AuthenticationService);
        this.authorizationService = service(AuthorizationService);
        this.navigationContextService = service(NavigationContextService);
        this.authStorageService = service(AuthenticationStorageService);
        this.runtimeConfigurationContextService = service(RuntimeConfigurationContextService);
        this.eventService = service(EventService);
        this.userPreferencesService = service(UserPreferencesService);
        this.userPreferencesContextService = service(UserPreferencesContextService);
        this.guideContextService = service(GuideContextService);
        this.fullJwtKey = `${StorageKey.GLOBAL_NAMESPACE}.${this.authStorageService.NAMESPACE}.${this.authStorageService.jwtKey}`;
        this.fullAuthenticatedKey = `${StorageKey.GLOBAL_NAMESPACE}.${this.authStorageService.NAMESPACE}.${this.authStorageService.authenticatedKey}`;
        this.authEvents = [this.fullJwtKey, this.fullAuthenticatedKey];
        this.isLowResolution = false;
        this.showHeader = this.isAuthenticatedFully();
        this.showNavbar = false;
        this.isEmbedded = false;
        this.loading = true;
        this.hideSolrDeprecationBanner = false;
        // ========================
        // Private
        // ========================
        /**
         * List of subscriptions to manage component lifecycle
         * */
        this.subscriptions = new SubscriptionList();
        this.isNavbarCollapsed = false;
        this.handleStorageChange = (event) => {
            const localStorageSubscriptionHandlerService = service(LocalStorageSubscriptionHandlerService);
            localStorageSubscriptionHandlerService.handleStorageChange(event);
            this.handleAuthChange(event.key);
        };
        this.windowResizeObserver = debounce(() => this.windowResizeHandler(), 50);
        WindowService.getWindow().addEventListener('storage', this.handleStorageChange);
    }
    componentDidRender() {
        this.windowResizeHandler();
    }
    componentDidLoad() {
        this.updateSolrDeprecationBannerVisibility();
    }
    connectedCallback() {
        this.subscribeToSecurityChanges();
        this.updateVisibility();
        this.subscribeToNavigationEnd();
        this.subscribeToRuntimeConfigurationChanges();
        this.subscribeToUserPreferencesChange();
        this.subscribeToBeforeMountRouting();
        this.subscribeToGuideChanges();
    }
    /**
     * Lifecycle method called when the component is about to be removed from the DOM.
     * Unsubscribes from all subscriptions to prevent memory leaks.
     */
    disconnectedCallback() {
        this.subscriptions.unsubscribeAll();
    }
    render() {
        return (h(Host, { key: '93d3603643075eb2a4945b888eeaac62e685bfe7', class: {
                'wb-layout': true,
                'is-embedded': this.isEmbedded,
                'hide-navbar': !this.showNavbar,
                'loading': this.loading
            } }, h("div", { key: '930b6c16100c669127eb7268262daebb9fd6c0fa', class: "default-slot-wrapper" }, h("slot", { key: 'ef953bd8ca9edc5248393dbd6c12570bc3b6bfbf', name: "default" })), !this.isEmbedded && h("div", { key: '04cb51b944088fe848843550be49b1b8a05fa0df', class: "wb-header" }, !this.hideSolrDeprecationBanner &&
            h("onto-deprecation-banner", { key: '83edeb755e3906aca3be4d429191d270ee8a5a80', class: 'onto-deprecation-banner', onCloseBanner: this.onSolrDeprecationBannerClosedHandler() }, h("span", { key: '670ba418372767b6054c8b9fc266f65936fd436f', slot: 'content' }, h("translate-label", { key: '29c439bb00ee2eaa28c1d036445f4dab448f9f3d', labelKey: 'deprecation-banner.solr.content' }))), h("header", { key: '10a7b76988637d94ee7693837c1cf3b8e504124b' }, this.showHeader && h("onto-header", { key: '63c0bc68b7debffdf604fce33a34ffd98ec07074' }))), !this.isEmbedded && this.showNavbar &&
            h("nav", { key: '77876485dacfe0b598332460d61beb88ea00aad3', class: "wb-navbar" }, h("onto-navbar", { key: '723251f904721da0d401778c89298e18b347a271', ref: this.assignNavbarRef(), "navbar-collapsed": this.isLowResolution })), h("slot", { key: 'ad9d76dff5292459a55a8501aae9123620793bd2', name: "main" }, h("onto-loader", { key: 'b6d2e86f2724b39a4ba558d010cda9ea486cab98', loading: this.loading, targetSelector: 'main', size: 96, messageText: TranslationService.translate('common.loading') })), !this.isEmbedded &&
            h("footer", { key: '52ffe0691773857e1d88ef17a09bb8661264c471', class: "wb-footer" }, h("onto-footer", { key: '50ab89b23214fdcfa5585638f8e956875a27f5f7' }))));
    }
    // ========================
    // Event Listeners
    // ========================
    /**
     * Event listener for the navbar toggled event. The layout needs to respond properly when the navbar is toggled in
     * order to fit the content.
     * @param event
     */
    onNavbarToggled(event) {
        this.isNavbarCollapsed = event.detail.payload;
        if (this.isNavbarCollapsed) {
            this.hostElement.classList.add('expanded');
        }
        else {
            this.hostElement.classList.remove('expanded');
        }
    }
    /**
     * Event listener for window resize event. We need to handle this in order to allow the navbar to collapse when the
     * window is resized.
     */
    onResize() {
        this.windowResizeObserver();
    }
    onSolrDeprecationBannerClosedHandler() {
        return () => {
            this.userPreferencesService.dismissSolrDeprecationBanner();
        };
    }
    // ========================
    // Handlers
    // ========================
    windowResizeHandler() {
        var _a, _b, _c, _d;
        this.isLowResolution = WindowService.getWindow().innerWidth <= WINDOW_WIDTH_FOR_COLLAPSED_NAVBAR;
        const navbar = document.querySelector('.navbar-component');
        if (navbar) {
            const cookieBanner = document.querySelector('.cookie-consent-banner');
            const header = document.querySelector('.wb-header');
            const headerHeight = (_b = (_a = header === null || header === void 0 ? void 0 : header.getBoundingClientRect()) === null || _a === void 0 ? void 0 : _a.height) !== null && _b !== void 0 ? _b : 0;
            const bannerHeight = (_d = (_c = cookieBanner === null || cookieBanner === void 0 ? void 0 : cookieBanner.getBoundingClientRect()) === null || _c === void 0 ? void 0 : _c.height) !== null && _d !== void 0 ? _d : 0;
            navbar.style.height = `calc(100% - ${headerHeight}px - ${bannerHeight}px)`;
        }
        if (!this.isLowResolution && !this.isNavbarCollapsed) {
            this.hostElement.classList.remove('expanded');
        }
        else {
            this.hostElement.classList.add('expanded');
        }
    }
    handleAuthChange(changedKey) {
        var _a;
        if (!changedKey) {
            return;
        }
        if (this.authEvents.includes(changedKey)) {
            const authenticated = this.authStorageService.isAuthenticated();
            if (authenticated) {
                navigate((_a = this.navigationContextService.getPreviousRoute()) !== null && _a !== void 0 ? _a : './');
            }
            else {
                navigateToLoginPage();
            }
            WindowService.getWindow().location.reload();
        }
    }
    // ========================
    // Security & Permissions
    // ========================
    subscribeToSecurityChanges() {
        const securityContextService = service(SecurityContextService);
        this.subscriptions.add(securityContextService.onAuthenticatedUserChanged((authenticatedUser) => {
            this.authenticatedUser = authenticatedUser;
            this.updateVisibility();
        }));
        this.subscriptions.add(securityContextService.onSecurityConfigChanged((securityConfig) => {
            this.securityConfig = securityConfig;
            this.updateVisibility();
        }));
        this.subscriptions.add(this.eventService.subscribe(EventName.LOGIN, () => {
            this.setNavbarItemVisibility();
            this.updateVisibility();
        }));
        this.subscriptions.add(this.eventService.subscribe(EventName.LOGOUT, () => {
            this.setNavbarItemVisibility();
            this.updateVisibility();
            navigateToLoginPage();
        }));
    }
    subscribeToRuntimeConfigurationChanges() {
        this.subscriptions.add(this.runtimeConfigurationContextService.onRuntimeConfigurationChanged((config) => {
            var _a;
            this.isEmbedded = (_a = config === null || config === void 0 ? void 0 : config.isEmbedded) !== null && _a !== void 0 ? _a : false;
        }));
    }
    updateVisibility() {
        this.updateSolrDeprecationBannerVisibility();
        if (!this.authenticationService.isSecurityEnabled()) {
            this.showNavbar = true;
            this.showHeader = true;
        }
        else {
            this.showHeader = this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
            this.showNavbar = this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
        }
    }
    isAuthenticatedFully() {
        return !this.authenticationService.isSecurityEnabled() || this.authenticationService.isAuthenticated() || this.authorizationService.hasFreeAccess();
    }
    shouldShowMenu(role) {
        return this.isAuthenticatedFully() && this.authorizationService.hasRole(role);
    }
    // ========================
    // Navbar logic
    // ========================
    assignNavbarRef() {
        return (navbar) => {
            this.navbarRef = navbar;
            if (!this.navbarRef) {
                return;
            }
            this.navbarRef.menuItems = WindowService.getPluginRegistry().get(MainMenuExtensionPoint.NAME);
            this.setNavbarItemVisibility();
        };
    }
    setNavbarItemVisibility() {
        var _a, _b, _c;
        if (!((_a = this.navbarRef) === null || _a === void 0 ? void 0 : _a.menuItems)) {
            return;
        }
        // recursively check for children and set their shouldShow property
        const processItem = (item) => {
            var _a;
            item.shouldShow = this.shouldShowMenu(item.role);
            if ((_a = item.children) === null || _a === void 0 ? void 0 : _a.length) {
                item.children.forEach(processItem);
            }
        };
        (_c = (_b = this.navbarRef) === null || _b === void 0 ? void 0 : _b.menuItems) === null || _c === void 0 ? void 0 : _c.forEach(plugin => {
            plugin.items.forEach(processItem);
        });
        // Update the reference to trigger a re-render
        this.navbarRef.menuItems = [...this.navbarRef.menuItems];
    }
    subscribeToNavigationEnd() {
        this.subscriptions.add(this.eventService.subscribe(EventName.NAVIGATION_END, (navigationEndPayload) => {
            this.navigationContextService.updatePreviousRoute(navigationEndPayload.oldUrl);
            // NAVIGATION_END appears to happen in the same tick as BeforeMountRouting, so the loader never gets shown
            setTimeout(() => {
                this.loading = false;
            });
        }));
    }
    subscribeToBeforeMountRouting() {
        this.subscriptions.add(this.eventService.subscribe(EventName.BEFORE_MOUNT_ROUTING_EVENT, () => {
            this.loading = true;
        }));
    }
    subscribeToUserPreferencesChange() {
        this.subscriptions.add(this.userPreferencesContextService.onUserPreferencesChanged(() => {
            this.updateSolrDeprecationBannerVisibility();
        }));
    }
    subscribeToGuideChanges() {
        this.subscriptions.add(this.guideContextService.onHasRunningGuideChanged(() => this.updateSolrDeprecationBannerVisibility()));
    }
    updateSolrDeprecationBannerVisibility() {
        return this.hideSolrDeprecationBanner = this.userPreferencesService.isSolrDeprecationBannerDismissed() || this.guideContextService.hasRunningGuide();
    }
    get hostElement() { return getElement(this); }
};
OntoLayout.style = ontoLayoutCss;

export { OntoLayout as onto_layout };
